import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Heart, Briefcase, Building2, Scale, Shield, FileText, Gavel,
  Users, Home as HomeIcon, Lightbulb, ArrowLeft, ArrowRight,
  Check, MessageCircle, CalendarCheck,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import SEO, { orgJsonLd, breadcrumbJsonLd, faqJsonLd } from "../components/SEO";
import { SERVICES, getServiceBySlug } from "../data/services";
import { getPageWaLink } from "../data/firm";

const ICONS = { Heart, Briefcase, Building2, Scale, Shield, FileText, Gavel, Users, Home: HomeIcon, Lightbulb };

export default function ServiceDetail() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const svc = getServiceBySlug(slug);
  if (!svc) return <Navigate to="/services" replace />;

  const Icon = ICONS[svc.icon] || Scale;
  const path = `/services/${svc.slug}`;
  const related = SERVICES.filter((s) => s.slug !== svc.slug).slice(0, 3);
  const waLink = getPageWaLink(svc.slug, lang);

  return (
    <div data-testid={`service-detail-${svc.slug}`}>
      <SEO
        path={path}
        titleAr={svc.seoTitleAr}
        titleEn={svc.seoTitleEn}
        descriptionAr={svc.metaAr}
        descriptionEn={svc.metaEn}
        jsonLd={[
          orgJsonLd(),
          breadcrumbJsonLd([
            { name: isAr ? "الرئيسية" : "Home", path: "/" },
            { name: isAr ? "خدماتنا" : "Services", path: "/services" },
            { name: isAr ? svc.titleAr : svc.titleEn, path },
          ]),
          faqJsonLd(isAr ? svc.faqAr : svc.faqEn),
        ]}
      />

      {/* HERO */}
      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe">
          <nav className="text-xs mb-6" style={{ color: "var(--ink-soft)" }}>
            <Link to="/" className="hover:underline">{isAr ? "الرئيسية" : "Home"}</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:underline">{isAr ? "خدماتنا" : "Services"}</Link>
            <span className="mx-2">/</span>
            <span style={{ color: "var(--heading)" }}>{isAr ? svc.titleAr : svc.titleEn}</span>
          </nav>
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <div className="w-14 h-14 rounded-sm flex items-center justify-center mb-6"
                   style={{ background: "var(--bg)", color: "var(--gold-deep)", border: "1px solid var(--gold)" }}>
                <Icon className="w-7 h-7" />
              </div>
              <p className="eyebrow">{isAr ? "خدماتنا القانونية" : "Legal services"}</p>
              <h1 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule`}
                  style={{ fontSize: "clamp(2rem, 4.5vw, 3.25rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)", lineHeight: 1.2 }}>
                {isAr ? svc.titleAr : svc.titleEn}
              </h1>
              <p className={`mt-8 ${isAr ? "font-arabic" : ""} text-lg leading-relaxed`} style={{ color: "var(--ink-soft)" }}>
                {isAr ? svc.introAr : svc.introEn}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid={`service-${svc.slug}-book`}>
                  <Button className="btn-gold rounded-sm px-7 h-12">
                    <CalendarCheck className="w-4 h-4 me-2" />
                    {isAr ? "احجز استشارة عبر واتساب" : "Book via WhatsApp"}
                  </Button>
                </a>
                <a href={waLink} target="_blank" rel="noopener noreferrer"
                   data-testid={`service-${svc.slug}-whatsapp`}>
                  <Button className="btn-whatsapp rounded-sm px-7 h-12">
                    <MessageCircle className="w-4 h-4 me-2" />
                    {isAr ? "واتساب" : "WhatsApp"}
                  </Button>
                </a>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="luxe-card p-6" style={{ background: "var(--bg)" }}>
                <p className="eyebrow mb-3">{isAr ? "خدمات هذا المجال" : "What's included"}</p>
                <ul className="space-y-2">
                  {(isAr ? svc.includesAr : svc.includesEn).slice(0, 6).map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--gold-deep)" }} />
                      <span className={isAr ? "font-arabic" : ""} style={{ color: "var(--ink)" }}>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INCLUDES + DOCUMENTS */}
      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe grid lg:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow">{isAr ? "كيف نساعدك" : "How we help"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} text-2xl lg:text-3xl`}
                style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "خدمات تفصيلية في هذا المجال" : "Detailed services in this practice"}
            </h2>
            <ul className="mt-8 space-y-3">
              {(isAr ? svc.includesAr : svc.includesEn).map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 mt-2.5 rounded-full shrink-0" style={{ background: "var(--gold)" }} />
                  <span className={`${isAr ? "font-arabic" : ""} leading-relaxed`} style={{ color: "var(--ink)" }}>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow">{isAr ? "المستندات المطلوبة" : "Documents needed"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} text-2xl lg:text-3xl`}
                style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "ما ستحتاج إلى تجهيزه" : "What you'll need to prepare"}
            </h2>
            <ul className="mt-8 space-y-3">
              {(isAr ? svc.documentsAr : svc.documentsEn).map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 mt-2.5 rounded-full shrink-0" style={{ background: "var(--gold)" }} />
                  <span className={`${isAr ? "font-arabic" : ""} leading-relaxed`} style={{ color: "var(--ink)" }}>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow">{isAr ? "آلية العمل" : "Our process"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule center text-2xl lg:text-3xl`}
                style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "أربع خطوات واضحة" : "Four clear steps"}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(isAr ? svc.processAr : svc.processEn).map((p) => (
              <div key={p.step} className="luxe-card p-6" style={{ background: "var(--bg)" }}>
                <div className="font-display text-5xl mb-4" style={{ color: "var(--gold-soft)" }}>{p.step}</div>
                <h3 className={`${isAr ? "font-arabic" : "font-display"} text-lg`}
                    style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>{p.title}</h3>
                <p className={`mt-3 ${isAr ? "font-arabic" : ""} text-sm leading-relaxed`}
                   style={{ color: "var(--ink-soft)" }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe max-w-4xl">
          <div className="text-center mb-12">
            <p className="eyebrow">{isAr ? "أسئلة شائعة" : "FAQ"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule center text-2xl lg:text-3xl`}
                style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "أسئلة العملاء الأكثر تكراراً" : "What clients most often ask"}
            </h2>
          </div>
          <div className="space-y-4">
            {(isAr ? svc.faqAr : svc.faqEn).map((f) => (
              <details key={f.q} className="luxe-card p-5 group">
                <summary className={`${isAr ? "font-arabic" : ""} cursor-pointer font-medium list-none flex items-start justify-between gap-4`}
                         style={{ color: "var(--heading)" }}>
                  <span>{f.q}</span>
                  <span className="text-xl shrink-0 transition-transform group-open:rotate-45"
                        style={{ color: "var(--gold-deep)" }}>+</span>
                </summary>
                <p className={`${isAr ? "font-arabic" : ""} mt-3 text-sm leading-relaxed`}
                   style={{ color: "var(--ink-soft)" }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED + CTA */}
      <section className="section-y" style={{ background: "var(--beige)" }}>
        <div className="container-luxe">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="eyebrow">{isAr ? "خدمات ذات صلة" : "Related services"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} text-2xl lg:text-3xl`}
                style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "قد تهمك أيضاً" : "You may also need"}
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((r) => {
              const RIcon = ICONS[r.icon] || Scale;
              return (
                <Link key={r.slug} to={`/services/${r.slug}`}
                      className="luxe-card p-6" style={{ background: "var(--bg)" }}>
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center mb-4"
                       style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                    <RIcon className="w-5 h-5" />
                  </div>
                  <h3 className={`${isAr ? "font-arabic" : "font-display"} text-base`}
                      style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>
                    {isAr ? r.titleAr : r.titleEn}
                  </h3>
                  <p className={`mt-2 ${isAr ? "font-arabic" : ""} text-sm`} style={{ color: "var(--ink-soft)" }}>
                    {isAr ? r.teaserAr : r.teaserEn}
                  </p>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-14">
            <a href={waLink} target="_blank" rel="noopener noreferrer">
              <Button className="btn-gold rounded-sm px-8 h-12">
                {isAr ? "احجز استشارة الآن عبر واتساب" : "Book via WhatsApp now"}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
